import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon } from "@iconify/react";
import { statusStyles } from "../../utils/adminHelpers";
import TableMui from "../../components/common/TableMui";
import { FiltersIcon } from "../../components/CustomIcons";
import CompanyModal from "../../components/admin/CompanyModal";
import DeleteModal from "../../components/common/DeleteModal";
import FilterModal from "../../components/common/FilterModal";
import Searchbox from "../../components/common/Searchbox";
import { companyAdminService } from "../../services/api/companyAdminService";
import { planService } from "../../services/api/planService";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/formatters";

const CompaniesAndUsers = () => {
  const [show, setShow] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [search, setSearch] = useState("");
  const [filtersQuery, setFiltersQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchCompanyAdmins = useCallback(
    async (p = 1, s = "", f = "") => {
      const currentRequestId = ++requestIdRef.current;
      setLoading(true);
      try {
        let q = `companyAdmins?page=${p}&limit=${limit}`;
        if (s) q += `&search=${encodeURIComponent(s)}`;
        if (f) q += `&${String(f).replace(/^\?/, "")}`;

        const response = await companyAdminService.list({
          page: p,
          limit,
          search: s || undefined,
          ...(f ? Object.fromEntries(new URLSearchParams(f)) : {}),
        });

        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        if (response && (response.results || response.message)) {
          const {
            results = [],
            totalResults: respTotal = 0,
            page: respPage,
          } = response;

          const formattedData = (results || []).map((user) => {
            const statusValue = user.status
              ? user.status.charAt(0).toUpperCase() + user.status.slice(1)
              : user.isActive
              ? "Active"
              : "Inactive";

            return {
              id: user._id,
              name:
                `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                "N/A",
              email: user.email,
              role: user.planId?.name || "N/A",
              status: statusValue,
              checkerStatus: formatDate(user.createdAt),
              profilePicture: user.profilePicture,
              contact: user.contact,
              companyName: user.companyName,
              rawData: user,
            };
          });

          if (!isMountedRef.current) return;

          setTableData(formattedData || []);
          setTotalResults(Number(respTotal || 0));

          if (typeof respPage === "number" && respPage !== p) {
            setPage(Number(respPage));
          }
        } else {
          if (isMountedRef.current) {
            toast.error(
              response?.message || "Failed to fetch company admins"
            );
            setTableData([]);
            setTotalResults(0);
          }
        }
      } catch (error) {
        if (requestIdRef.current !== currentRequestId) return;
        console.error("Error fetching company admins:", error);
        if (isMountedRef.current) {
          toast.error("An error occurred while fetching data");
          setTableData([]);
          setTotalResults(0);
        }
      } finally {
        if (requestIdRef.current === currentRequestId && isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [limit]
  );

  useEffect(() => {
    fetchCompanyAdmins(page, search, filtersQuery);
  }, [page, search, filtersQuery, fetchCompanyAdmins]);

  const handleEdit = (row) => {
    setSelectedCompany(row.rawData);
    setModalMode("edit");
    setShow(true);
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await companyAdminService.delete(deleteId);
      toast.success("Company admin deleted successfully");
      setShowDeleteModal(false);
      setDeleteId(null);
      fetchCompanyAdmins(page, search, filtersQuery);
    } catch (error) {
      console.error("Error deleting company admin:", error);
      toast.error("An error occurred while deleting");
    } finally {
      if (isMountedRef.current) setDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleModalClose = (shouldRefresh = false) => {
    setShow(false);
    setSelectedCompany(null);
    if (shouldRefresh) {
      setPage(1);
      fetchCompanyAdmins(1, search, filtersQuery);
    }
  };

  const handlePageChange = (_, newPage) => {
    if (
      typeof newPage === "number" &&
      Number.isFinite(newPage) &&
      newPage > 0
    ) {
      setPage(newPage);
      return;
    }
    const maybeIndex =
      (newPage && newPage.selected) ??
      (newPage && newPage.page) ??
      (newPage && newPage.current) ??
      null;
    if (typeof maybeIndex === "number" && Number.isFinite(maybeIndex)) {
      const resolved = maybeIndex + 1;
      if (resolved > 0) setPage(resolved);
    }
  };

  const handleSearch = useCallback((value) => {
    setPage(1);
    setSearch(value);
  }, []);

  const filterFields = [
    {
      name: "status",
      type: "select",
      label: "Status",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
        { label: "Blocked", value: "blocked" },
        { label: "Deleted", value: "deleted" },
        { label: "Pending", value: "pending" },
      ],
      placeholder: "All",
    },
    {
      name: "sortBy",
      type: "select",
      label: "Sort By",
      options: [
        { label: "Created At (desc)", value: "createdAt:desc" },
        { label: "Created At (asc)", value: "createdAt:asc" },
      ],
      placeholder: "Default",
    },
    {
      name: "projectBy",
      type: "text",
      label: "Project By (comma-separated fields)",
      placeholder: "e.g. firstName,email,status",
    },
  ];

  const handleFilterApply = (queryString) => {
    const cleaned = (queryString || "").replace(/^\?/, "");
    setPage(1);
    setFiltersQuery(cleaned);
  };

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Total Companies</p>
          <p className="text-xl font-semibold text-text-dark mt-2">{totalResults}</p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Active Companies</p>
          <p className="text-xl font-semibold text-text-dark mt-2">
            {tableData.filter((item) => item.status === "Active").length}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Inactive Companies</p>
          <p className="text-xl font-semibold text-text-dark mt-2">
            {tableData.filter((item) => item.status === "Inactive").length}
          </p>
        </div>
        <div className="p-4 rounded-xl bg-white border border-border-light">
          <p className="text-text-secondary text-sm">Current Page</p>
          <p className="text-xl font-semibold text-text-dark mt-2">
            {page} / {Math.ceil(totalResults / limit) || 1}
          </p>
        </div>
      </div>

      <div className="mt-4 bg-white border border-border-light p-4 rounded-xl">
        <div className="flex flex-col md:flex-row items-center py-4 justify-between gap-2">
          <p className="text-text-dark text-sm font-semibold">Company Admins</p>
          <div className="flex items-center gap-2 flex-col md:flex-row justify-end md:w-[700px]">
            <Searchbox
              placeholder="Search companies by name / email / company"
              onSearch={handleSearch}
              delay={600}
            />

            <div className="flex justify-between w-full md:w-fit md:justify-end gap-3">
              <button
                onClick={() => setFilterOpen(true)}
                className={`flex items-center gap-2 px-4 rounded-lg border border-border-light h-12 ${
                  filtersQuery ? "text-primary" : "text-text-dark"
                }`}
              >
                Filters
                <FiltersIcon />
                {filtersQuery && (
                  <Icon
                    icon="mdi:close"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFiltersQuery("");
                    }}
                    className="w-5 h-5 text-red-500 cursor-pointer"
                  />
                )}
              </button>

              <button
                className="text-sm font-semibold bg-primary text-white py-3 px-4 rounded-lg whitespace-nowrap hover:bg-primary-dark"
                onClick={() => {
                  setSelectedCompany(null);
                  setModalMode("add");
                  setShow(true);
                }}
              >
                Add New Company
              </button>
            </div>
          </div>
        </div>
        <div className="my-3">
          <TableMui
            loading={loading}
            th={{
              name: "User Name",
              email: "Email",
              role: "Subscription Plan",
              status: "Status",
              checkerStatus: "Date Created",
              action: "",
            }}
            td={tableData}
            pagination={true}
            page={page}
            limit={limit}
            totalResults={totalResults}
            onPageChange={handlePageChange}
            customFields={[
              {
                name: "name",
                data: (value, row) => {
                  return (
                    <div className="inline-flex gap-2 items-center">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                        {value.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-text-dark font-medium">{value}</p>
                        {row.companyName && (
                          <p className="text-text-secondary text-xs">
                            {row.companyName}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                },
              },
              {
                name: "status",
                data: (value) => {
                  const styles = statusStyles[value] || {
                    text: "text-gray-400",
                    bg: "bg-gray-100",
                  };
                  return (
                    <div
                      className={`inline-flex gap-2 items-center rounded-full py-1 px-3 ${styles.text} ${styles.bg}`}
                    >
                      <Icon icon="mdi:circle" className="text-xs" />
                      {value}
                    </div>
                  );
                },
              },
              {
                name: "action",
                data: (value, row) => {
                  return (
                    <div className="inline-flex gap-3 items-center">
                      <Icon
                        icon="mdi:delete-outline"
                        className="cursor-pointer text-lg text-text-secondary hover:text-red-500 transition-colors"
                        onClick={() => openDeleteModal(row.id)}
                      />
                      <Icon
                        icon="mdi:pencil-outline"
                        className="cursor-pointer text-lg text-text-secondary hover:text-text-dark transition-colors"
                        onClick={() => handleEdit(row)}
                      />
                    </div>
                  );
                },
              },
            ]}
          />
        </div>
      </div>

      {show && (
        <CompanyModal
          show={show}
          onHide={handleModalClose}
          modalMode={modalMode}
          companyData={selectedCompany}
          onSuccess={() => handleModalClose(true)}
        />
      )}

      <DeleteModal
        show={showDeleteModal}
        onHide={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Company Admin"
        message="Are you sure you want to delete this company admin? This action cannot be undone."
        loading={deleting}
      />

      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        fields={filterFields}
        onApply={(q) => handleFilterApply(q)}
      />
    </div>
  );
};

export default CompaniesAndUsers;
